<template>
  <div v-if="!loading && !viewIsLoading">
    <new-contact-form
      :showModal="showModal"
      @close="closeModal"
      @addNewContact="addClientContact"
    />
    <b-modal
      ref="rejection-modal"
      hide-header
      centered
      size="sm"
      :ok-title="$t('actions.submit')"
      :cancel-title="$t('actions.cancel')"
      footer-class="d-flex justify-content-center"
      :ok-disabled="!checkRejectionReason"
      @ok="manageProposalStatus(false)"
      @hidden="closeRejectionModal"
    >
      <div>
        <b-form-group
          :label="$t('field.rejection_reason') + ':'"
          class="font-weight-bold fs-16"
        >
          <b-form-textarea
            v-model="rejectionReason"
            name="name"
            type="text"
            no-resize
          />
        </b-form-group>
      </div>
    </b-modal>
    <b-row class="mb-3">
      <b-col class="d-flex justify-content-between">
        <div class="d-inline-flex align-items-center" v-if="!externalApproval">
          <span class="mr-3">{{ $t("field.status") }}:</span>
          <b-form-input
            v-if="items.status"
            v-model="items.status.name"
            name="status"
            disabled
          />
        </div>
        <div
          class="statusDropdown d-flex align-items-center"
          v-if="externalApproval"
        >
          <span class="mr-3">{{ $t("field.status") }}:</span>
          <multi-select
            v-model="items.status"
            :multiple="false"
            :options="statuses"
            :allow-empty="false"
            :searchable="false"
            :placeholder="$t('actions.pick_status')"
            :show-labels="false"
            label="name"
            track-by="id"
            @input="updateExternalApproval"
          />
          <b-button
            variant="unstyled"
            class="p-0 d-inline-flex justify-content-center ml-2"
            v-b-popover.hover.top="$t('messages.external_approval')"
          >
            <font-awesome-icon
              :icon="faInfoCircle"
              class="text-primary fs-18"
            />
          </b-button>
        </div>
        <div
          class="d-inline-flex align-items-center"
          v-if="waitForApprovalStatus && userCanApprove"
        >
          <b-button
            variant="success"
            @click.stop="manageProposalStatus(true)"
            >{{ $t("actions.approve") }}</b-button
          >
          <b-button
            variant="danger"
            class="ml-2"
            @click.stop="openRejectionModal"
            >{{ $t("actions.reject") }}</b-button
          >
        </div>
        <div
          class="d-inline-flex align-items-center"
          v-if="manageInputsButtons"
        >
          <b-button
            variant="primary"
            @click.stop="manageProposalInputs('create')"
            class="d-flex align-items-center"
            ><font-awesome-icon :icon="faPlusSquare" class="mr-1" />{{
              $t("actions.create")
            }}</b-button
          >
          <b-button
            variant="primary"
            class="ml-2 d-flex align-items-center"
            @click.stop="manageProposalInputs('copy')"
            ><font-awesome-icon :icon="faCopy" class="mr-1" />{{
              $t("actions.new_copy")
            }}</b-button
          >
        </div>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <b-form-group :label="$t('field.responsible')">
          <b-form-input
            v-model="budgetItem.user.name"
            name="responsible"
            disabled
          />
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group :label="$t('field.responsible_contact')">
          <b-form-input
            v-model="budgetItem.user.phone"
            name="responsible"
            disabled
          />
        </b-form-group>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <b-form-group :label="$t('field.client')">
          <b-form-input
            v-model="budgetItem.client.name"
            name="client"
            disabled
          />
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group :label="$t('field.clientContact')">
          <b-button
            v-if="!disableProposal"
            @click.stop="openModal"
            variant="primary"
            class="contactBtn p-2 position-absolute"
          >
            <font-awesome-icon :icon="faPlusSquare" class="mr-1" />
            {{ $t("list.new") }}
          </b-button>
          <multi-select
            v-model="clientContact"
            :multiple="false"
            :options="clientContacts"
            :searchable="true"
            :placeholder="$t('actions.pick_contact')"
            :show-labels="false"
            label="name"
            track-by="name"
            @input="updateClientContact"
            :disabled="disableProposal"
          >
            <span slot="noOptions">{{ $t("messages.no_results") }}</span>
            <span slot="noResult">{{ $t("messages.no_results") }}</span>
          </multi-select>
        </b-form-group>
      </b-col>
    </b-row>
    <b-row>
      <b-form-group :label="$t('field.proposal_date')" class="col-3">
        <b-form-input
          v-model="$v.date.$model"
          :class="{ 'border border-danger': $v.date.$error }"
          name="date"
          type="date"
          @input="updateProposalDate"
          required
          locale="pt-PT"
          :disabled="disableProposal"
        />
      </b-form-group>
    </b-row>
    <b-form-group :label="$t('field.subject')">
      <b-form-input
        v-model="$v.subject.$model"
        :class="{ 'border border-danger': $v.subject.$error }"
        name="subject"
        type="text"
        required
        placeholder="Lorem ipsum set dolor"
        :disabled="disableProposal"
      />
    </b-form-group>
    <b-form-group :label="$t('field.products')" class="position-relative">
      <proposal-products
        @updateTotal="updateTotal"
        :disableProposal="disableProposal"
      />
    </b-form-group>
    <b-form-group :label="$t('field.proposal_value')">
      <b-row>
        <b-col class="pr-1 col-3">
          <b-input-group append="€">
            <b-form-input
              v-model="items.total_price"
              name="total_price"
              disabled
            />
          </b-input-group>
        </b-col>
        <b-col class="pl-0">
          <b-form-input
            v-model="$v.total_price_text.$model"
            :class="{ 'border border-danger': $v.total_price_text.$error }"
            name="total_price_text"
            type="text"
            required
            :disabled="disableProposal"
          />
        </b-col>
      </b-row>
    </b-form-group>
    <b-row>
      <b-col>
        <b-form-group :label="$t('field.deadline')">
          <multi-select
            v-model="$v.deadline.$model"
            :class="{
              'border border-danger rounded': $v.deadline.$error,
            }"
            :multiple="false"
            :options="deadlineOptions"
            :allow-empty="false"
            :searchable="true"
            :placeholder="$t('actions.pick_deadline')"
            :show-labels="false"
            label="label"
            track-by="label"
            @input="updateDeadline"
            :disabled="disableProposal"
          >
            <span slot="noOptions">{{ $t("messages.no_results") }}</span>
            <span slot="noResult">{{ $t("messages.no_results") }}</span>
          </multi-select>
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group :label="$t('field.expiration_date')">
          <multi-select
            v-model="$v.expiration_date.$model"
            :class="{
              'border border-danger rounded': $v.expiration_date.$error,
            }"
            :multiple="false"
            :options="expirationDateOptions"
            :allow-empty="false"
            :searchable="true"
            :placeholder="$t('actions.pick_expirationDate')"
            :show-labels="false"
            label="label"
            track-by="label"
            @input="updateExpirationDate"
            :disabled="disableProposal"
          >
            <span slot="noOptions">{{ $t("messages.no_results") }}</span>
            <span slot="noResult">{{ $t("messages.no_results") }}</span>
          </multi-select>
        </b-form-group>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <b-form-group :label="$t('field.conditions')">
          <multi-select
            v-model="$v.conditions.$model"
            :class="{
              'border border-danger rounded': $v.conditions.$error,
            }"
            :multiple="false"
            :options="conditionsOptions"
            :allow-empty="false"
            :searchable="true"
            :placeholder="$t('actions.pick_conditions')"
            :show-labels="false"
            label="label"
            track-by="label"
            @input="updateConditions"
            :disabled="disableProposal"
          >
            <span slot="noOptions">{{ $t("messages.no_results") }}</span>
            <span slot="noResult">{{ $t("messages.no_results") }}</span>
          </multi-select>
        </b-form-group>
      </b-col>
    </b-row>
    <b-row>
      <b-col class="col-8">
        <b-form-group :label="$t('field.payment_term')">
          <multi-select
            v-model="$v.paymentOption.$model"
            :class="{
              'border border-danger rounded': $v.paymentOption.$error,
            }"
            :multiple="false"
            :options="paymentTermOptions"
            :allow-empty="false"
            :searchable="true"
            :placeholder="$t('actions.pick_term')"
            :show-labels="false"
            label="label"
            track-by="label"
            @input="updatePaymentTerm"
            :disabled="disableProposal"
          >
            <span slot="noOptions">{{ $t("messages.no_results") }}</span>
            <span slot="noResult">{{ $t("messages.no_results") }}</span>
          </multi-select>
        </b-form-group>
      </b-col>
      <b-col class="col-4">
        <b-form-group :label="$t('field.financial_discount')">
          <b-input-group append="%">
            <b-form-input
              v-model="$v.financial_discount.$model"
              :class="{
                'border border-danger rounded': $v.financial_discount.$error,
              }"
              name="financial_discount"
              type="number"
              :disabled="disableProposal"
            />
          </b-input-group>
        </b-form-group>
      </b-col>
    </b-row>
    <editor
      :inputField="items.notes"
      :fieldName="$t('field.notes')"
      @updateInput="updateNotes"
      :disableProposal="disableProposal"
    />
    <editor
      :inputField="items.other_fields"
      :fieldName="$t('field.other_fields')"
      @updateInput="updateOtherFields"
      :disableProposal="disableProposal"
    />
    <div
      class="mt-3 align-items-end d-flex justify-content-end"
      v-if="hasItems"
    >
      <b-dropdown
        variant="primary"
        right
        :text="$t('actions.options')"
        class="m-md-2 options"
      >
        <b-dropdown-item
          v-if="draftStatus && isResponsibleUser"
          class="p-0 text-primary"
          @click.stop="updateProposal()"
        >
          {{ $t("list.save") }}
        </b-dropdown-item>
        <b-dropdown-item
          v-if="draftStatus && isResponsibleUser"
          class="p-0 text-primary"
          @click.stop="updateProposal('download')"
        >
          {{ $t("actions.save_export") }}
        </b-dropdown-item>
        <b-dropdown-item
          v-if="draftStatus && isResponsibleUser && !externalApproval"
          class="p-0 text-primary"
          @click.stop="updateProposal('status', 2)"
        >
          {{ $t("actions.save_send_approval") }}
        </b-dropdown-item>
        <b-dropdown-item
          v-if="!draftStatus"
          class="p-0 text-primary"
          @click.stop="download(items.id)"
        >
          {{ $t("actions.export") }}
        </b-dropdown-item>
        <b-dropdown-item
          class="p-0 text-primary"
          v-if="waitForApprovalStatus && isResponsibleUser && !externalApproval"
          @click.stop="updateProposalStatus(5)"
        >
          {{ $t("actions.cancel") }}
        </b-dropdown-item>
      </b-dropdown>
    </div>
  </div>
  <div v-else>
    <spinner />
  </div>
</template>

<script>
import { required, maxValue, minValue } from 'vuelidate/lib/validators'
import { faPlusSquare, faCopy } from '@fortawesome/free-regular-svg-icons'
import {
  faSave,
  faFilePdf,
  faAngleDown,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import relationship from '@/mixins/relationship'
import downloadPDF from '@/mixins/downloadPDF'
import clientsApi from '@/api/clients'
import contactsApi from '@/api/contacts'
import api from '@/api/proposal'
import budgetApi from '@/api/budgets'
import statusesApi from '@/api/statuses'
import workflowsApi from '@/api/workflows'
import model from '@/models/proposal'
import { mapGetters } from 'vuex'
import MultiSelect from 'vue-multiselect'
import {
  paymentTermOptions,
  expirationDateOptions,
  supplyConditionsOptions,
  deadlineOptions
} from '@/constants/options'
import newContactForm from '@/components/budgets/proposal/newContactForm'
import editor from '@/components/common/editor'
import proposalProducts from '@/components/budgets/proposal/proposalProducts'
import moment from 'moment'

export default {
  props: {
    viewIsLoading: {
      type: Boolean,
      required: true
    }
  },
  components: {
    MultiSelect,
    FontAwesomeIcon,
    newContactForm,
    editor,
    proposalProducts
  },
  mixins: [relationship, downloadPDF],
  watch: {
    '$store.state.form.fields.client_id': {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.clientContact = []
          this.getContacts()
          this.setClientContact()
        }
      }
    },
    '$store.state.form.fields.workflow_id': {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.getWorkflows()
        }
      }
    }
  },
  data: () => ({
    model: {
      ...JSON.parse(JSON.stringify(model.empty))
    },
    paymentTermOptions,
    expirationDateOptions,
    supplyConditionsOptions,
    deadlineOptions,
    api,
    clientsApi,
    contactsApi,
    statusesApi,
    workflowsApi,
    budgetApi,
    faPlusSquare,
    faInfoCircle,
    faCopy,
    faSave,
    faFilePdf,
    faAngleDown,
    editor: null,
    loading: true,
    paymentOption: null,
    clientContacts: [],
    clientContact: null,
    date: null,
    subject: null,
    total_price_text: null,
    deadline: null,
    conditions: null,
    conditionsOptions: [],
    expiration_date: null,
    financial_discount: null,
    relationshipField: 'budget_id',
    relationshipTitle: 'name',
    showModal: false,
    workflowsList: [],
    workflow: [],
    statuses: [],
    userPermission: false,
    findUser: null,
    disableProposal: false,
    rejectionReason: null,
    pendingProposal: [],
    userCanApprove: false,
    manageInputsButtons: false,
    createNewProposal: false
  }),
  computed: {
    ...mapGetters({
      items: 'form/relationshipFields',
      budgetItem: 'form/fields'
    }),
    filterContacts() {
      return {
        client_id: this.budgetItem.client_id
      }
    },
    waitForApprovalStatus() {
      return this.items.status_id === 2
    },
    draftStatus() {
      return this.items.status_id === 1 || !this.items.status
    },
    isResponsibleUser() {
      return this.budgetItem.user_id === this.$auth.user().id
    },
    checkRejectionReason() {
      return this.rejectionReason && this.rejectionReason.length
    },
    hasItems() {
      return !(!this.isResponsibleUser && this.draftStatus)
    },
    externalApproval() {
      return this.workflow.number_approvals === null
    }
  },
  created() {
    this.initComponent()
  },
  methods: {
    async initComponent() {
      await this.init()

      this.loading = true

      await this.initRelationshipFromSpecificField(
        this.budgetItem.id,
        'budget_id'
      )

      await Promise.all([
        this.getWorkflows(),
        this.getContacts(),
        this.getStatuses(),
        this.getProposalsToApprove()
      ])

      this.setClientContact()
      this.setPaymentTerm()
      this.setSupplyConditions()
      this.setFields()
      this.checkProposalStatus()

      this.loading = false
    },
    async getContacts() {
      try {
        const response = await this.contactsApi.all(this.filterContacts)

        if (response && response.data && response.data.list) {
          this.clientContacts = response.data.list
        } else {
          this.$toasted.error(this.$t('messages.unknown_error'))
        }
      } catch (e) {
        console.error(e)
      }
    },
    async getStatuses() {
      try {
        const response = await this.statusesApi.all()

        if (response && response.data && response.data.list) {
          this.statuses = response.data.list
        } else {
          this.$toasted.error(this.$t('messages.unknown_error'))
        }
      } catch (e) {
        console.error(e)
      }
    },
    async getWorkflows() {
      try {
        const response = await this.workflowsApi.all()

        if (response && response.data && response.data.list) {
          this.workflowsList = response.data.list
          this.workflow = this.workflowsList.find(
            workflow => workflow.id === this.budgetItem.workflow_id
          )
        } else {
          this.$toasted.error(this.$t('messages.unknown_error'))
        }
      } catch (e) {
        console.error(e)
      }
    },
    async getProposalsToApprove() {
      try {
        const response = await this.api.pending(this.$auth.user().id)

        if (response && response.data && response.data.list) {
          this.pendingProposal = response.data.list.filter(
            proposal => proposal.id === this.items.id
          )

          if (this.pendingProposal.length) {
            this.userCanApprove = true
          } else {
            this.userCanApprove = false
          }
        } else {
          this.$toasted.error(this.$t('messages.unknown_error'))
        }
      } catch (e) {
        console.error(e)
      }
    },
    updateClientContact(contact) {
      this.clientContact = contact
      this.items.contact_id = contact.id
      this.items.contact = contact
    },
    setClientContact() {
      if (this.clientContacts.length && this.items.contact) {
        this.clientContact = this.items.contact
      }
    },
    setPaymentTerm() {
      this.paymentOption = this.paymentTermOptions.filter(
        item => item.label === this.items.payment_term
      )
    },
    setFields() {
      this.expiration_date = this.items.expiration_date
        ? { label: this.items.expiration_date }
        : null
      this.date = !this.items.date
        ? moment().format('YYYY-MM-DD')
        : this.items.date
      this.subject = this.items.subject
      this.total_price_text = this.items.total_price_text
      this.commercial_discount = this.items.commercial_discount * 100
      this.financial_discount = this.items.financial_discount
        ? this.items.financial_discount * 100
        : null
      this.deadline = this.items.deadline
        ? { label: this.items.deadline }
        : null
      this.conditions = this.items.conditions
        ? { label: this.items.conditions }
        : null
    },
    setSupplyConditions() {
      this.conditionsOptions = this.supplyConditionsOptions
    },
    updateProposalDate(date) {
      this.date = date
      this.items.date = this.date
    },
    updatePaymentTerm(paymentOption) {
      this.paymentOption = paymentOption
      this.items.payment_term = paymentOption.label
      this.financial_discount = paymentOption.discount
        ? paymentOption.discount * 100
        : null
    },
    updateExpirationDate(date) {
      this.expiration_date = date
      this.items.expiration_date = date.label
    },
    updateConditions(conditions) {
      this.conditions = conditions
      this.items.conditions = conditions.label
    },
    updateDeadline(date) {
      this.deadline = date
      this.items.deadline = date.label
    },
    formatProduct(product) {
      if (product.pivot && Object.keys(product.pivot).length) {
        return {
          ...product,
          product_id: product.id,
          price_unit: product.pivot.price_unit,
          price_package: product.pivot.price_package,
          position: product.pivot.position,
          batch: product.pivot.batch,
          entity_code: product.pivot.entity_code,
          client_code: product.pivot.client_code,
          quantity: product.pivot.quantity,
          quantity_package: product.pivot.quantity_package,
          quantity_per_package: product.quantity_per_package,
          commercial_discount: product.pivot.commercial_discount,
          total_price: product.pivot.total_price,
          other_fields: product.pivot.other_fields,
          observations: product.pivot.observations
        }
      }

      return product
    },
    async updateProposal(type, id) {
      this.loading = true
      this.$v.$touch()

      this.items.date = this.date
      this.items.contact = this.clientContact
      this.items.subject = this.subject
      this.items.total_price_text = this.total_price_text
      this.items.commercial_discount = this.commercial_discount / 100
      this.items.financial_discount = this.financial_discount
        ? this.financial_discount / 100
        : null

      if (this.$v.$invalid) {
        this.$toasted.error(this.$t('form.error'))
        this.loading = false
        return
      } else {
        if (this.items.products && this.items.products.length) {
          this.items.products = this.items.products.map(product =>
            this.formatProduct(product)
          )

          this.checkIfNewProposal()

          const responseFromAddRelationshipItem = await this.addRelationshipItem()

          await this.initRelationshipFromSpecificField(
            this.budgetItem.id,
            'budget_id'
          )

          if (
            type === 'download' &&
            responseFromAddRelationshipItem.length === 0
          ) {
            await this.download(this.items.id)
          }

          if (
            type === 'status' &&
            responseFromAddRelationshipItem.length === 0
          ) {
            await this.updateProposalStatus(id)
          }
        } else {
          this.$toasted.error(this.$t('form.product_error'))
        }
      }

      this.setFields()
      this.createNewProposal = false
      this.loading = false
    },
    checkIfNewProposal() {
      if (this.createNewProposal === true) {
        this.relationshipItem.id = null
      }
    },
    openModal() {
      this.showModal = true
    },
    openRejectionModal() {
      this.$refs['rejection-modal'].show()
    },
    closeModal() {
      this.showModal = false
    },
    closeRejectionModal() {
      this.$refs['rejection-modal'].hide()
    },
    async addClientContact(name, email, phone) {
      this.loading = true

      const newContact = {
        name: name,
        email: email,
        phone: phone,
        contactable_type: 'App\\Models\\Client',
        contactable_id: this.budgetItem.client_id
      }

      try {
        const response = await this.contactsApi.create({ ...newContact })

        if (response && response.data) {
          this.$toasted.success(
            this.$t('messages.created', { item: 'Contacto' })
          )
          await this.getContacts()
        } else if (
          response &&
          response.response &&
          response.response.data &&
          response.response.data.errors
        ) {
          this.$toasted.error(
            Object.values(response.response.data.errors).join(' ')
          )
        } else {
          this.$toasted.error(this.$t('messages.unknown_error'))
        }
      } catch (e) {
        console.error(e)
      }

      this.showModal = false
      this.clientContact = null
      this.loading = false
    },
    updateOtherFields(text) {
      this.items.other_fields = text
    },
    updateNotes(text) {
      this.items.notes = text
    },
    updateTotal(total) {
      this.items.total_price = Number(total).toFixed(2)
    },
    checkProposalStatus() {
      if (
        (!this.items.status_id || this.items.status_id === 1) &&
        this.isResponsibleUser
      ) {
        this.disableProposal = false
      } else {
        this.disableProposal = true
      }

      if (!this.items.status_id) {
        this.items.status = this.statuses.find(status => status.id === 1)
        this.items.status_id = 1
      }

      if (
        this.items.status_id &&
        (this.items.status_id === 4 || this.items.status_id === 5)
      ) {
        this.manageInputsButtons = true
      } else {
        this.manageInputsButtons = false
      }

      this.loading = false
    },
    async updateProposalStatus(status_id) {
      this.loading = true

      try {
        const response = await this.api.updateStatus({
          proposal_id: this.items.id,
          status_id: status_id
        })

        if (response && response.data && response.data) {
          this.$toasted.success(this.$t('messages.updated', { item: 'Estado' }))

          if (status_id === 2 && this.workflow.number_approvals === 0) {
            this.items.status_id = 3
            status_id = 3
          } else {
            this.items.status_id = status_id
          }

          this.items.status = this.statuses.find(
            status => status.id === status_id
          )
        } else {
          this.$toasted.error(this.$t('messages.unknown_error'))
        }
      } catch (e) {
        console.error(e)
      }

      this.checkProposalStatus()
      this.loading = false
    },
    async manageProposalStatus(isApproved) {
      this.loading = true

      try {
        const response = await this.api.userApprove({
          id: this.items.id,
          is_approved: isApproved,
          rejection_reason: !this.isApproved ? this.rejectionReason : null
        })

        if (response && response.data && response.data) {
          this.$toasted.success(this.$t('messages.updated', { item: 'Estado' }))
          if (isApproved) {
            this.items.status_id = 3
            this.items.status = this.statuses.find(status => status.id === 3)
          } else {
            this.items.status_id = 4
            this.items.status = this.statuses.find(status => status.id === 4)
          }
        } else {
          this.$toasted.error(this.$t('messages.unknown_error'))
        }
      } catch (e) {
        console.error(e)
      }

      this.checkProposalStatus()
      this.loading = false
    },
    async manageProposalInputs(type) {
      this.loading = true

      await this.updateProposalStatus(1)
      this.disableProposal = false
      this.createNewProposal = true
      this.manageInputsButtons = false

      if (type === 'create') {
        await this.resetRelationShipItem()
        this.$v.$reset()
        this.clientContact = null
        this.paymentOption = null
        this.setFields()
      }

      this.items.status_id = 1
      this.items.status = this.statuses.find(status => status.id === 1)

      this.loading = false
    },
    updateExternalApproval(status) {
      this.updateProposalStatus(status.id)
    }
  },
  validations: {
    subject: {
      required
    },
    expiration_date: {
      required
    },
    total_price_text: {
      required
    },
    deadline: {
      required
    },
    date: {
      required
    },
    conditions: {
      required
    },
    paymentOption: {
      required
    },
    financial_discount: {
      minValue: minValue(0),
      maxValue: maxValue(100)
    }
  }
}
</script>
<style lang="scss" scoped>
.contactBtn {
  right: 1rem;
  top: -0.3rem;
}

.input {
  border-top: none;
  border-left: none;
  border-right: none;

  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &[type="number"] {
    -moz-appearance: textfield;
  }
}

.statusDropdown {
  width: 30%;
}
</style>